import { AGGREGATION_TYPE, VALUE_DIFFERENCE_DISPLAY } from 'components/View/constants';
import { createDefaultDataQuarterSelection } from 'components/View/filters/DataQuarterFilter/helpers';

import { CMASummaryFilters } from './types';
import { COMMUNITY_CONTRACT_SELECTION_TYPE, CONTRACT_DISPLAY_TYPES } from '../../constants';

export const CMA_SUMMARY_DATA_ROW_TYPE = {
  COMMUNITY_NAME: 'COMMUNITY_NAME',
  ADDRESS: 'ADDRESS',
  DISTANCE: 'DISTANCE',
  WEBSITE: 'WEBSITE',
  PHONE: 'PHONE',
  YEAR_OPENED: 'YEAR_OPENED',
  NOTES: 'COMMUNITY_NOTES',
  REVIEWS: 'REVIEWS',
  CARE_TYPES: 'CARE_TYPES',
  CONTRACT_TYPE: 'CONTRACT_TYPE',
  OCCUPANCY: 'OCCUPANCY',
  NUMBER_OF_REVIEWS: 'NUMBER_OF_REVIEWS',
  INCENTIVES: 'INCENTIVES',
  CONTRACT_BENEFITS: 'CONTRACT_BENEFITS',
  SHOP_DATE: 'SHOP_DATE',
} as const;

export const CMA_SUMMARY_REVIEW_TYPE_DATA_ROW = 'CMA_SUMMARY_REVIEW_TYPE_DATA_ROW';
export const CMA_SUMMARY_LIVING_TYPE_DATA_ROW = 'CMA_SUMMARY_LIVING_TYPE_DATA_ROW';
export const CMA_SUMMARY_LIVING_TYPE_FEE_DATA_ROW = 'CMA_SUMMARY_LIVING_TYPE_FEE_DATA_ROW';
export const CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW = 'CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW';
export const CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW_ROOM_COMPARISON =
  'CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW_ROOM_COMPARISON';
export const CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW = 'CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW';
export const CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW_ROOM_COMPARISON =
  'CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW_ROOM_COMPARISON';
export const CMA_SUMMARY_CARE_FEE_GROUP_DATA_ROW = 'CMA_SUMMARY_CARE_FEE_GROUP_DATA_ROW';
export const CMA_SUMMARY_CARE_FEE_DATA_ROW = 'CMA_SUMMARY_CARE_FEE_DATA_ROW';
export const CMA_SUMMARY_MEDICATION_FEE_GROUP_DATA_ROW = 'CMA_SUMMARY_MEDICATION_FEE_GROUP_DATA_ROW';
export const CMA_SUMMARY_MEDICATION_FEE_DATA_ROW = 'CMA_SUMMARY_MEDICATION_FEE_DATA_ROW';
export const CMA_SUMMARY_CONTRACT_BENEFIT_DATA_ROW = 'CMA_SUMMARY_CONTRACT_BENEFIT_DATA_ROW';
export const CMA_SUMMARY_COMMUNITY_CONTRACT_ROW = 'CMA_SUMMARY_COMMUNITY_CONTRACT_ROW';

export const CMA_SUMMARY_TABLE_COLUMNS_OFFSET = 2;
export const CMA_SUMMARY_COMPETITOR_AVERAGE_LABEL = 'Average (Competitors)';
export const CMA_SUMMARY_NOT_APPLICABLE_VALUE = 'N/A';
export const CMA_SUMMARY_NON_AVERAGEABLE_ROW_TYPES = [
  CMA_SUMMARY_DATA_ROW_TYPE.ADDRESS,
  CMA_SUMMARY_DATA_ROW_TYPE.WEBSITE,
  CMA_SUMMARY_DATA_ROW_TYPE.PHONE,
  CMA_SUMMARY_DATA_ROW_TYPE.YEAR_OPENED,
  CMA_SUMMARY_DATA_ROW_TYPE.NOTES,
  CMA_SUMMARY_DATA_ROW_TYPE.SHOP_DATE,
  CMA_SUMMARY_DATA_ROW_TYPE.CARE_TYPES,
  CMA_SUMMARY_DATA_ROW_TYPE.CONTRACT_TYPE,
  CMA_SUMMARY_DATA_ROW_TYPE.INCENTIVES,
  CMA_SUMMARY_CONTRACT_BENEFIT_DATA_ROW,
  CMA_SUMMARY_COMMUNITY_CONTRACT_ROW,
] as const;

type ViewOptionKeys = keyof CMASummaryFilters['viewOptions'];
type CMASummaryDataRowType = (typeof CMA_SUMMARY_DATA_ROW_TYPE)[keyof typeof CMA_SUMMARY_DATA_ROW_TYPE];

export const CONTACT_INFO_FIELDS: {
  key: ViewOptionKeys;
  title: string;
  type: CMASummaryDataRowType;
}[] = [
  { key: 'address', title: 'Address', type: CMA_SUMMARY_DATA_ROW_TYPE.ADDRESS },
  { key: 'distance', title: 'Distance', type: CMA_SUMMARY_DATA_ROW_TYPE.DISTANCE },
  { key: 'website', title: 'Website', type: CMA_SUMMARY_DATA_ROW_TYPE.WEBSITE },
  { key: 'phone', title: 'Phone', type: CMA_SUMMARY_DATA_ROW_TYPE.PHONE },
];

export const CMA_SUMMARY_DATA_FEE_INCENTIVE_ROW_TYPE: Record<
  string,
  { id: string; label: string; field: ViewOptionKeys }
> = {
  COMMUNITY_FEE: {
    id: 'COMMUNITY_FEE',
    label: 'Community Fee',
    field: 'communityFee',
  },
  SECOND_PERSON_FEE: {
    id: 'SECOND_PERSON_FEE',
    label: 'Second Person Mth. Fee',
    field: 'secondPersonFee',
  },
  PET_FEE: {
    id: 'PET_FEE',
    label: 'Pet Fee',
    field: 'petFee',
  },
  PARKING_FEE: {
    id: 'PARKING_FEE',
    label: 'Parking Fee',
    field: 'parkingFee',
  },
  RESPITE_FEE: {
    id: 'RESPITE_FEE',
    label: 'Respite Fee',
    field: 'respiteFee',
  },
  ADDITIONAL_FEE: {
    id: 'ADDITIONAL_FEE',
    label: 'Additional Fee',
    field: 'additionalFee',
  },
  MOVE_IN_FEE: {
    id: 'MOVE_IN_FEE',
    label: 'Move In Fee',
    field: 'moveInFee',
  },
  SECOND_PERSON_ENTRANCE_FEE: {
    id: 'SECOND_PERSON_ENTRANCE_FEE',
    label: '2nd Person Entrance Fee',
    field: 'secondPersonEntranceFee',
  },
  DEPOSIT: {
    id: 'DEPOSIT',
    label: 'Deposit',
    field: 'deposit',
  },
  EMERGENCY_PENDANT_FEE: {
    id: 'EMERGENCY_PENDANT_FEE',
    label: 'Emergency Pendant Fee',
    field: 'emergencyPendantFee',
  },
};

export const CMA_SUMMARY_ROOM_COMPARISON_TYPE_FILTER_OPTIONS = {
  SIMPLE_COMPARISON: 1,
  ADVANCED_COMPARISON: 2,
};

export const CMA_SUMMARY_ROOM_TYPE_FILTER_COMPARISON_OPTIONS = {
  BY_ROOM_TYPE: 1,
  BY_ROOM_TYPE_AND_SQFT: 2,
} as const;

export const CMA_SUMMARY_ROOM_COMPARISON_SQFT_RANGE_OPTIONS = [
  { value: 100, label: '100 sq ft' },
  { value: 200, label: '200 sq ft' },
  { value: 300, label: '300 sq ft' },
];

export const CMA_SUMMARY_ROOM_COMPARISON_DEFAULT_SQFT_RANGE = CMA_SUMMARY_ROOM_COMPARISON_SQFT_RANGE_OPTIONS[0].value;

export const CMA_SUMMARY_ROOM_COMPARISON_TYPE_FILTER_ROW_FIELDS = {
  RATE: 'Rate',
  CARE: 'Care',
  MEDICATION: 'Medication',
};

export const CMA_SUMMARY_ROOM_DATA_FILTER_TYPES = {
  AVG: {
    id: 1,
    label: 'Avg',
  },
  MIN: {
    id: 2,
    label: 'Min',
  },
  MAX: {
    id: 3,
    label: 'Max',
  },
};

export const CMA_SUMMARY_ROOM_COMPARISON_TYPE_FILTER_ROW_FIELD_TYPE = {
  [CMA_SUMMARY_ROOM_COMPARISON_TYPE_FILTER_ROW_FIELDS.RATE]: [
    CMA_SUMMARY_ROOM_DATA_FILTER_TYPES.AVG,
    CMA_SUMMARY_ROOM_DATA_FILTER_TYPES.MIN,
    CMA_SUMMARY_ROOM_DATA_FILTER_TYPES.MAX,
  ],
  [CMA_SUMMARY_ROOM_COMPARISON_TYPE_FILTER_ROW_FIELDS.CARE]: [
    CMA_SUMMARY_ROOM_DATA_FILTER_TYPES.AVG,
    CMA_SUMMARY_ROOM_DATA_FILTER_TYPES.MIN,
    CMA_SUMMARY_ROOM_DATA_FILTER_TYPES.MAX,
  ],
  [CMA_SUMMARY_ROOM_COMPARISON_TYPE_FILTER_ROW_FIELDS.MEDICATION]: [
    CMA_SUMMARY_ROOM_DATA_FILTER_TYPES.AVG,
    CMA_SUMMARY_ROOM_DATA_FILTER_TYPES.MIN,
    CMA_SUMMARY_ROOM_DATA_FILTER_TYPES.MAX,
  ],
};

export const CMA_SUMMARY_ROOM_TYPE_GROUPING = {
  GROUP: 1,
  DONT_GROUP: 2,
} as const;

export const CMA_SUMMARY_ROOM_TYPE_GROUPING_LABELS = {
  [CMA_SUMMARY_ROOM_TYPE_GROUPING.GROUP]: 'Group',
  [CMA_SUMMARY_ROOM_TYPE_GROUPING.DONT_GROUP]: "Don't Group",
} as const;

export const INDEPENDENT_LIVING_TYPE = 'Independent Living';
export const ROOM_COMPARISON_ROWS = 'roomComparisonRows';
export const ROOM_COMPARISON_TYPE = 'roomComparisonType';
export const CMA_SUMMARY_ROOM_COMPARISON_MINIMUM_FILTER_FIELDS = 2;
export const INITIAL_DISPLAYED_NUMBER_OF_COMMUNITIES = 5;
export const INITIAL_DISPLAYED_NUMBER_OF_COMPETITORS = 6;
export const CMA_SUMMARY_TABLE_COLUMN_WIDTH = 280;
export const CMA_SUMMARY_ROOM_COMPARISON_FILTER_BASE_ROW = [
  [{ name: 'Rate', fieldType: CMA_SUMMARY_ROOM_DATA_FILTER_TYPES.AVG.id }],
];

export const CMA_SUMMARY_VIEW_FILTER_DISPLAY_COMPETITOR = {
  DISPLAY_ALL: 0,
  PAGINATE: 1,
};

export const CMA_SUMMARY_INITIAL_FILTERS: CMASummaryFilters = {
  roomComparisonType: CMA_SUMMARY_ROOM_COMPARISON_TYPE_FILTER_OPTIONS.SIMPLE_COMPARISON,
  roomComparisonRows: [],
  roomComparisonSqftRange: CMA_SUMMARY_ROOM_COMPARISON_DEFAULT_SQFT_RANGE,
  roomComparisonShowClosestBeyondRange: false,
  roomTypeFilterComparisonType: CMA_SUMMARY_ROOM_TYPE_FILTER_COMPARISON_OPTIONS.BY_ROOM_TYPE,
  roomTypesPriceDisplay: AGGREGATION_TYPE.AVG,
  competitors: [],
  reviewSources: [],
  careTypes: [],
  roomTypes: [],
  roomTypeGrouping: CMA_SUMMARY_ROOM_TYPE_GROUPING.GROUP,
  careLevels: [],
  careLevelsStatsType: AGGREGATION_TYPE.AVG,
  medicationLevels: [],
  medicationLevelsStatsType: AGGREGATION_TYPE.AVG,
  contractTypes: [],
  viewOptions: {
    contactInformation: true,
    address: true,
    website: true,
    distance: true,
    phone: true,
    yearOpened: true,
    communityNotes: true,
    contractBenefits: true,
    reviews: true,
    careTypes: true,
    ratesFees: true,
    roomType: true,
    squareFootage: true,
    pricePerSquareFootage: true,
    communityFee: true,
    shopDate: true,
    occupancy: true,
    petFee: true,
    secondPersonFee: true,
    parkingFee: true,
    respiteFee: true,
    additionalFee: true,
    moveInFee: true,
    secondPersonEntranceFee: true,
    deposit: true,
    emergencyPendantFee: true,
    careFee: true,
    medicationFee: true,
    feeNote: true,
    medicationFeeNote: true,
    incentives: true,
    averageCompetitors: true,
    valueDisplay: VALUE_DIFFERENCE_DISPLAY.DONT_DISPLAY,
    competitorDisplayMode: CMA_SUMMARY_VIEW_FILTER_DISPLAY_COMPETITOR.DISPLAY_ALL,
  },
  contractDisplayType: CONTRACT_DISPLAY_TYPES.MY_CONTRACTS,
  contractSelectionType: COMMUNITY_CONTRACT_SELECTION_TYPE.APPROXIMATE,
  communityContractSlugs: [],
  dataQuarterSelection: createDefaultDataQuarterSelection(),
};

export const FEE_ENTRY_TYPES = {
  CARE_FEES: 1,
  MEDICATION_FEE: 2,
};

export const CMA_SUMMARY_FEE_TITLES = {
  CARE: 'Care Fees',
  MEDICATION: 'Medication Fees',
};

export const CMA_SUMMARY_FEE_LABELS = {
  CARE: 'Care Fee',
  MEDICATION: 'Medication Fee',
};

export const DEPENDENCIES: Record<string, string[]> = {
  contactInformation: ['address', 'distance', 'website', 'phone', 'yearOpened', 'communityNotes'],
  ratesFees: [
    'contractBenefits',
    'roomType',
    'squareFootage',
    'pricePerSquareFootage',
    'communityFee',
    'petFee',
    'secondPersonFee',
    'careFee',
    'medicationFee',
    'feeNote',
    'medicationFeeNote',
    'parkingFee',
    'respiteFee',
    'additionalFee',
    'moveInFee',
    'secondPersonEntranceFee',
    'deposit',
    'emergencyPendantFee',
  ],
  roomType: ['squareFootage', 'pricePerSquareFootage'],
  careFee: ['feeNote'],
  medicationFee: ['medicationFeeNote'],
};
